import { VStack, Text, HStack, Spacer, Box } from "@chakra-ui/react";
import { MessageFragment, MessageThreadFragment } from "gql/graphql";
import React, { memo, useMemo } from "react";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import colors from "app/colors";
import { ForwardIcon, TranslateIcon } from "modules/icons";
import MessageText from "modules/messaging/messages/message-text";
import MessageAvatarHeader from "modules/messaging/messages/message-avatar-header";
import moment from "moment";
import {
  MESSAGE_CALENDAR_FORMATS,
  MESSAGE_STATE,
} from "modules/utils/Constants";
import MessageFiles from "modules/messaging/messages/message-files";
import MessagePopover from "modules/messaging/messages/message-popover/message-popover";
import MessageReactions from "modules/messaging/messages/message-reactions";
import LinkedConversationsPopover from "modules/messaging/messages/linked-conversations-popover";
import MessageSeen from "modules/messaging/messages/message-seen";
import UserAvatar from "modules/_shared/user-avatar";
import { MESSAGE_SEEN_HOVER_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  messageState: MESSAGE_STATE;
}

const HappycoMessage: React.FC<IProps> = ({
  messageThread,
  message,
  messageState,
}) => {
  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const reactions = useMemo(
    () => message.reactionList.filter((reaction) => !reaction.fileId),
    [message],
  );

  const relatedMessageThreads = message.relatedMessageThreadList;

  return (
    <VStack alignItems="flex-start" spacing={0} w="full" pb={4}>
      <HStack spacing={0} w="full">
        <MessageAvatarHeader color={colors.messageBackground}>
          <UserAvatar userId={message.createdUserId} size="sm" />
        </MessageAvatarHeader>
        <HStack w="full" pt={3}>
          <Text color={colors.blackSecondary} fontSize={14} fontWeight={600}>
            HappyCo Updates
          </Text>
          <Spacer />
          <Text
            color={colors.grayMain}
            fontSize={12}
            fontWeight={400}
            whiteSpace="nowrap"
          >
            {moment(message.createTime).calendar(MESSAGE_CALENDAR_FORMATS)}
          </Text>
        </HStack>
      </HStack>
      <MessagePopover
        message={message}
        messageThread={messageThread}
        placement="top-end"
        placementOffset={[6, -20]}
        messageState={messageState}
      >
        <Box
          w="full"
          alignItems="flex-start"
          bg={colors.messageBackground}
          pt="10px"
          pr="12px"
          pb="10px"
          pl={isViewingTranslatedMessage ? "4px" : "12px"}
          roundedTopRight="2xl"
          roundedBottomRight="2xl"
          roundedBottomLeft="2xl"
        >
          <HStack>
            {isViewingTranslatedMessage && (
              <TranslateIcon boxSize={3} fill={colors.grayMain} mt={1} />
            )}
            <Box
              color={colors.blackSecondary}
              fontWeight={500}
              fontSize={14}
              fontStyle="normal"
              sx={{ margin: "0 !important" }}
            >
              <MessageText messageThread={messageThread} message={message} />
            </Box>
          </HStack>
          <MessageFiles
            messageThread={messageThread}
            message={message}
            messageState="sent"
          />
          <HStack>
            <HStack divider={<Box h={4} borderColor={colors.gray30} />}>
              {!!reactions.length && (
                <Box py={1} pl={isViewingTranslatedMessage ? 3 : 0}>
                  <MessageReactions message={message} />
                </Box>
              )}
              {!!relatedMessageThreads.length && (
                <LinkedConversationsPopover
                  relatedMessageThreads={relatedMessageThreads}
                >
                  <HStack py={1} spacing={0.5} color={colors.blue}>
                    <ForwardIcon fill={colors.blue} />
                    <Text fontSize="11px" color={colors.blue}>
                      {relatedMessageThreads.length}
                    </Text>
                  </HStack>
                </LinkedConversationsPopover>
              )}
            </HStack>
            <Spacer />
            <Box
              _hover={{ zIndex: MESSAGE_SEEN_HOVER_Z_INDEX }} // ensures the seen popover is always on top of the MessagePopover
            >
              <MessageSeen messageThread={messageThread} message={message} />
            </Box>
          </HStack>
        </Box>
      </MessagePopover>
    </VStack>
  );
};

export default memo(HappycoMessage);
